import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  Card,
  Tag,
  Spin,
  message
} from 'antd';
import {
  FilterOutlined,
  SearchOutlined,
  ClearOutlined,
  CloseOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { parseISO, format } from 'date-fns';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ISINFilterSidebar = ({
  visible,
  onClose,
  onApplyFilters,
  loading = false,
  currentFilters = {}
}) => {
  console.log('ISINFilterSidebar rendered with visible:', visible);
  const [form] = Form.useForm();
  const [filterCount, setFilterCount] = useState(0);

  // Predefined options for dropdowns
  const issuerTypes = [
    'Non PSU',
    'Public Sector Undertaking (PSU)'
  ];

  const sectors = [
    'Diversified',
    'Financial Services',
    'Chemicals',
    'Construction Materials',
    'Oil, Gas & Consumable Fuels',
    'Construction',
    'Utilities',
    'Metals & Mining',
    'Healthcare',
    'Telecommunication',
    'Automobile and Auto Components',
    'Power',
    'Capital Goods',
    'Services',
    'Realty',
    'Forest Materials',
    'Fast Moving Consumer Goods'
  ];
  const macroeconomicSector = [
    'Diversified',
    'Financial Services',
    'Commodities',
    'Energy',
    'Industrials',
    'Utilities',
    'Healthcare',
    'Telecommunication',
    'Consumer Discretionary',
    'Services',
    'Fast Moving Consumer Goods'
  ];


  const industries = [
    'Diversified',
    'Finance',
    'Fertilizers & Agrochemicals',
    'Banks',
    'Cement & Cement Products',
    'Chemicals & Petrochemicals',
    'Petroleum Products',
    'Construction',
    'Other Utilities',
    'Minerals & Mining',
    'Capital Markets',
    'Pharmaceuticals & Biotechnology',
    'Telecom - Services',
    'Automobiles',
    'Power',
    'Agricultural, Commercial & Construction Vehicles',
    'Commercial Services & Supplies',
    'Realty',
    'Paper, Forest & Jute Products',
    'Food Products',
    'Auto Components',
    'Insurance',
    'Transport Infrastructure'
  ];
  const basicIndustry = [
    'Diversified',
    'Multi-Product Companies',
    'Non-Banking Financial Company (NBFC)',
    'Fertilizers',
    'Public Sector Bank',
    'Housing Finance Company',
    'Other Financial Services',
    'Private Sector Bank',
    'Cement & Cement Products',
    'Commodity Chemicals',
    'Refineries & Marketing',
    'Investment Company',
    'Civil Construction',
    'Financial Institution',
    'Multi Utilities',
    'Industrial Minerals',
    'Asset Management Company',
    'Others',
    'Energy',
    'Pharmaceuticals',
    'Other Telecom Services',
    'Passenger Cars & Utility Vehicles',
    'Electric Utilities',
    'Commercial Vehicles',
    'Specialty Chemicals',
    'Trading & Distributors',
    'Residential, Commercial Projects',
    'Paper & Paper Products',
    'Telecom - Cellular & Fixed line services',
    'Dairy Products',
    'Auto Components & Equipments',
    'Tyres & Rubber Products',
    'Motor Vehicles',
    'Telecom - Infrastructure',
    'Life Insurance',
    'Pesticides & Agrochemicals',
    'Finance',
    'Other Capital Market related Services',
    'Engineering, Designing & Construction',
    'Power - Transmission',
    'Other Bank',
    'Basic Materials',
    'Utilities',
    'Toll bridge operator',
    'Holding Company'
  ];


  const instrumentTypes = [
    'Debentures', 'Bonds', 'Commercial Paper', 'Certificate of Deposit',
    'Government Securities', 'State Government Securities'
  ];

  const securityTypes = [
    'Secured', 'Unsecured', 'Government Backed', 'Asset Backed'
  ];

  const issueModes = [
    'Private Placement', 'Public Issue', 'Private Placement (non-EBP)',
    'Private Placement (EBP)',
  ];

  const couponBasis = [
    'Fixed Interest',
    'Zero Interest',
    'Variable-Others',
    'Variable - Index Linked'
  ];


  const couponTypes = [
    'Simple', 'Compounding'
  ];

  const paymentFrequencies = [
    'On Maturity', 'QUARTERLY', 'Once a year', 'Twice a year'
  ];

  const exchanges = [
    'NSE', 'BSE', 'NSE, BSE'
  ];

  const listingStatuses = [
    'CURRENT', 'SUSPENDED', 'DELISTED'
  ];

  const currencies = [
    'INR', 'USD', 'EUR', 'GBP'
  ];

  // Initialize form with current filters
  useEffect(() => {
    if (visible && currentFilters) {
      form.setFieldsValue({
        searchTerm: currentFilters.searchTerm || '',
        corporateIdentityNumber: currentFilters.corporateIdentityNumber || '',
        legalEntityIdentifier: currentFilters.legalEntityIdentifier || '',
        typeOfIssuerBasedOnOwnership: currentFilters.typeOfIssuerBasedOnOwnership || undefined,
        macroeconomicSector: currentFilters.macroeconomicSector || undefined,
        sector: currentFilters.sector || undefined,
        industry: currentFilters.industry || undefined,
        basicIndustry: currentFilters.basicIndustry || undefined,
        typeOfInstrument: currentFilters.typeOfInstrument || undefined,
        typeOfSecurity: currentFilters.typeOfSecurity || undefined,
        security: currentFilters.security || undefined,
        modeOfIssue: currentFilters.modeOfIssue || undefined,
        minFaceValue: currentFilters.minFaceValue || undefined,
        maxFaceValue: currentFilters.maxFaceValue || undefined,
        minIssuePrice: currentFilters.minIssuePrice || undefined,
        maxIssuePrice: currentFilters.maxIssuePrice || undefined,
        minIssueSize: currentFilters.minIssueSize || undefined,
        maxIssueSize: currentFilters.maxIssueSize || undefined,
        minCouponRate: currentFilters.minCouponRate || undefined,
        maxCouponRate: currentFilters.maxCouponRate || undefined,
        couponBasis: currentFilters.couponBasis || undefined,
        couponType: currentFilters.couponType || undefined,
        couponPaymentFrequency: currentFilters.couponPaymentFrequency || undefined,
        allotmentDateRange: currentFilters.allotmentDateFrom && currentFilters.allotmentDateTo ?
          [parseISO(currentFilters.allotmentDateFrom), parseISO(currentFilters.allotmentDateTo)] : undefined,
        redemptionRange: currentFilters.redemptionFrom && currentFilters.redemptionTo ?
          [parseISO(currentFilters.redemptionFrom), parseISO(currentFilters.redemptionTo)] : undefined,
        listingDateRange: currentFilters.listingDateFrom && currentFilters.listingDateTo ?
          [parseISO(currentFilters.listingDateFrom), parseISO(currentFilters.listingDateTo)] : undefined,
        stockExchange: currentFilters.stockExchange || undefined,
        listingStatus: currentFilters.listingStatus || undefined,
        currencyOfIssuance: currentFilters.currencyOfIssuance || undefined,
      });
    }
  }, [visible, currentFilters, form]);

  // Count active filters
  useEffect(() => {
    const values = form.getFieldsValue();
    let count = 0;

    Object.keys(values).forEach(key => {
      const value = values[key];
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0 && value.some(v => v !== null && v !== undefined)) {
            count++;
          }
        } else {
          count++;
        }
      }
    });

    setFilterCount(count);
  }, [form]);

  const handleApplyFilters = async () => {
    try {
      const values = await form.validateFields();

      // Transform date ranges to individual fields
      const filters = {
        ...values,
        allotmentDateFrom: values.allotmentDateRange?.[0] ? format(values.allotmentDateRange[0], 'yyyy-MM-dd') : undefined,
        allotmentDateTo: values.allotmentDateRange?.[1] ? format(values.allotmentDateRange[1], 'yyyy-MM-dd') : undefined,
        redemptionFrom: values.redemptionRange?.[0] ? format(values.redemptionRange[0], 'yyyy-MM-dd') : undefined,
        redemptionTo: values.redemptionRange?.[1] ? format(values.redemptionRange[1], 'yyyy-MM-dd') : undefined,
        listingDateFrom: values.listingDateRange?.[0] ? format(values.listingDateRange[0], 'yyyy-MM-dd') : undefined,
        listingDateTo: values.listingDateRange?.[1] ? format(values.listingDateRange[1], 'yyyy-MM-dd') : undefined,
        page: 0, // Reset to first page
        size: 12
      };

      // Remove the range fields
      delete filters.allotmentDateRange;
      delete filters.redemptionRange;
      delete filters.listingDateRange;

      onApplyFilters(filters);
      message.success('Filters applied successfully');
    } catch (error) {
      console.error('Filter validation error:', error);
    }
  };

  const handleClearFilters = () => {
    form.resetFields();
    setFilterCount(0);
    message.info('Filters cleared');
  };

  const handleReset = () => {
    form.resetFields();
    setFilterCount(0);
    onApplyFilters({});
    message.info('Filters reset and applied');
  };

  console.log('Rendering drawer with visible:', visible);

  // Add a simple test div to see if the component is rendering
  if (visible) {
    console.log('Drawer should be visible!');
  }

  return (
    <Drawer
      title={
        <Space>
          <FilterOutlined />
          <span>Advanced Filters</span>
          {filterCount > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {filterCount} active
            </Tag>
          )}
        </Space>
      }
      placement="right"
      width="35%"
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            size="small"
          />
        </Space>
      }
      bodyStyle={{ padding: '16px' }}
      style={{ zIndex: 1000 }}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          size="small"
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* Basic Search */}
            <Card size="small" title="Basic Search" style={{ marginBottom: 16 }}>
              <Form.Item name="searchTerm" label="Search Term">
                <Input
                  placeholder="Search ISIN, issuer, industry..."
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Card>

            {/* Issuer Details */}
            <Card size="small" title="Issuer Details" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="corporateIdentityNumber" label="Corporate Identity Number">
                    <Input placeholder="Enter CIN" allowClear />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="legalEntityIdentifier" label="Legal Entity Identifier">
                    <Input placeholder="Enter LEI" allowClear />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="typeOfIssuerBasedOnOwnership" label="Issuer Type">
                    <Select placeholder="Select issuer type" allowClear>
                      {issuerTypes.map(type => (
                        <Option key={type} value={type}>{type}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {/* Empty column for balance */}
                </Col>
              </Row>
            </Card>

            {/* Sector & Industry */}
            <Card size="small" title="Sector & Industry" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="macroeconomicSector" label="Macroeconomic Sector">
                    <Select placeholder="Select sector" allowClear>
                      {macroeconomicSector.map(sector => (
                        <Option key={sector} value={sector}>{sector}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="sector" label="Sector">
                    <Select placeholder="Select sector" allowClear>
                      {sectors.map(sector => (
                        <Option key={sector} value={sector}>{sector}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="industry" label="Industry">
                    <Select placeholder="Select industry" allowClear>
                      {industries.map(industry => (
                        <Option key={industry} value={industry}>{industry}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="basicIndustry" label="Basic Industry">
                    <Select placeholder="Select basic industry" allowClear>
                      {basicIndustry.map(industry => (
                        <Option key={industry} value={industry}>{industry}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Instrument Details */}
            <Card size="small" title="Instrument Details" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="nameOfTheInstrument" label="Instrument Name">
                    <Input placeholder="Enter instrument name" allowClear />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="typeOfInstrument" label="Instrument Type">
                    <Select placeholder="Select instrument type" allowClear>
                      {instrumentTypes.map(type => (
                        <Option key={type} value={type}>{type}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                {/* <Col span={12}>
                  <Form.Item name="typeOfSecurity" label="Security Type">
                    <Select placeholder="Select security type" allowClear>
                      {securityTypes.map(type => (
                        <Option key={type} value={type}>{type}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col> */}
                <Col span={12}>
                  <Form.Item name="security" label="Security">
                    <Select placeholder="Select security" allowClear>
                      {securityTypes.map(type => (
                        <Option key={type} value={type}>{type}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="seniorityInRepayment" label="Seniority">
                    <Select placeholder="Select seniority" allowClear>
                      <Option value="Senior">Senior</Option>
                      <Option value="Subordinate">Subordinate</Option>
                      <Option value="Mezzanine">Mezzanine</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="modeOfIssue" label="Mode of Issue">
                    <Select placeholder="Select issue mode" allowClear>
                      {issueModes.map(mode => (
                        <Option key={mode} value={mode}>{mode}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

              </Row>
            </Card>

            {/* Financial Details */}
            <Card size="small" title="Financial Details" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minFaceValue" label="Min Face Value">
                    <InputNumber
                      placeholder="Min"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxFaceValue" label="Max Face Value">
                    <InputNumber
                      placeholder="Max"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minIssuePrice" label="Min Issue Price">
                    <InputNumber
                      placeholder="Min"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxIssuePrice" label="Max Issue Price">
                    <InputNumber
                      placeholder="Max"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minIssueSize" label="Min Issue Size">
                    <InputNumber
                      placeholder="Min"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxIssueSize" label="Max Issue Size">
                    <InputNumber
                      placeholder="Max"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minAmountRaised" label="Min Amount Raised">
                    <InputNumber
                      placeholder="Min"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxAmountRaised" label="Max Amount Raised">
                    <InputNumber
                      placeholder="Max"
                      style={{ width: '100%' }}
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Coupon Details */}
            <Card size="small" title="Coupon Details" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="minCouponRate" label="Min Coupon Rate (%)">
                    <InputNumber
                      placeholder="Min"
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="maxCouponRate" label="Max Coupon Rate (%)">
                    <InputNumber
                      placeholder="Max"
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
              </Row>



              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="couponBasis" label="Coupon Basis">
                    <Select placeholder="Select coupon basis" allowClear>
                      {couponBasis.map(basis => (
                        <Option key={basis} value={basis}>{basis}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="couponType" label="Coupon Type">
                    <Select placeholder="Select coupon type" allowClear>
                      {couponTypes.map(type => (
                        <Option key={type} value={type}>{type}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="couponPaymentFrequency" label="Payment Frequency">
                    <Select placeholder="Select frequency" allowClear>
                      {paymentFrequencies.map(freq => (
                        <Option key={freq} value={freq}>{freq}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dailyCountConvention" label="Daily Count Convention">
                    <Select placeholder="Select convention" allowClear>
                      <Option value="Actual/actual">Actual/actual</Option>
                      <Option value="30/360">30/360</Option>
                      <Option value="Actual/360">Actual/360</Option>
                      <Option value="Actual/365">Actual/365</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="convertibility" label="Convertibility">
                    <Select placeholder="Select convertibility" allowClear>
                      <Option value="Type A: Non Convertible; Type B: Not Applicable">Non Convertible</Option>
                      <Option value="Type A: Convertible; Type B: Not Applicable">Convertible</Option>
                      <Option value="Type A: Partially Convertible; Type B: Not Applicable">Partially Convertible</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  {/* Empty column for balance */}
                </Col>
              </Row>
            </Card>

            {/* Dates */}
            {/* <Card size="small" title="Important Dates" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="allotmentDateRange" label="Allotment Date Range">
                    <RangePicker
                      style={{ width: '100%' }}
                      placeholder={['From', 'To']}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="redemptionRange" label="Redemption Date Range">
                    <RangePicker
                      style={{ width: '100%' }}
                      placeholder={['From', 'To']}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="listingDateRange" label="Listing Date Range">
                    <RangePicker
                      style={{ width: '100%' }}
                      placeholder={['From', 'To']}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                </Col>
              </Row>
            </Card> */}

            {/* Exchange & Listing */}
            <Card size="small" title="Exchange & Listing" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="stockExchange" label="Stock Exchange">
                    <Select placeholder="Select exchange" allowClear>
                      {exchanges.map(exchange => (
                        <Option key={exchange} value={exchange}>{exchange}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="listingStatus" label="Listing Status">
                    <Select placeholder="Select status" allowClear>
                      {listingStatuses.map(status => (
                        <Option key={status} value={status}>{status}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="currencyOfIssuance" label="Currency">
                    <Select placeholder="Select currency" allowClear>
                      {currencies.map(currency => (
                        <Option key={currency} value={currency}>{currency}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="certificateNos" label="Certificate Numbers">
                    <Input placeholder="Enter certificate numbers" allowClear />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Additional Details */}
            {/* <Card size="small" title="Additional Details" style={{ marginBottom: 16 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="registrar" label="Registrar">
                    <Input placeholder="Enter registrar" allowClear />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="debentureTrustee" label="Debenture Trustee">
                    <Input placeholder="Enter debenture trustee" allowClear />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="infrastructureCategory" label="Infrastructure Category">
                    <Select placeholder="Select category" allowClear>
                      <Option value="Yes">Yes</Option>
                      <Option value="No">No</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="objectOfIssue" label="Object of Issue">
                    <Input placeholder="Enter object of issue" allowClear />
                  </Form.Item>
                </Col>
              </Row>
            </Card> */}
          </div>

          {/* Action Buttons */}
          <div style={{ paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button
                icon={<ClearOutlined />}
                onClick={handleClearFilters}
                disabled={filterCount === 0}
              >
                Clear
              </Button>

              <Space>
                <Button onClick={handleReset}>
                  Reset & Apply
                </Button>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={handleApplyFilters}
                  loading={loading}
                >
                  Apply Filters
                </Button>
              </Space>
            </Space>
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default ISINFilterSidebar;
